import type { ColumnDef } from '@tanstack/react-table';
import type { WithdrawRequestEntity } from 'services/openapi';
import React from 'react';
import { RequestWithdrawStatus } from 'configs/constants';
import { subMonths, endOfDay, startOfDay } from 'date-fns';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'configs';
import { NumberUtils } from 'modules';
import { format } from 'date-fns';
import { RequestWithdrawStatusColor } from 'configs/constants';
import { formatDateTime } from 'utils/helpers/helpers';
import { Link, useLocation } from 'react-router-dom';
import { MainRouteHelpers, MainRouteKeys } from 'routes/enums';
import { Icon } from 'components';

export type FilterType = {
  transactionStatus:
    | 'Tất cả'
    | 'Thành công'
    | 'Từ chối'
    | 'Đang xử lý'
    | 'Đang chuyển tiền tự động'
    | 'Chuyển tiền tự động thất bại';
  fromTime: string | number;
  toTime: string | number;
  refreshData: number;
  pageSize: number;
  pageIndex: number;
};

const ListDetailLink = ({
  children,
  ...props
}: React.ComponentProps<typeof Link>) => {
  const location = useLocation();

  return (
    <Link
      {...props}
      state={{ returnTo: `${location.pathname}${location.search}` }}
    >
      {children}
    </Link>
  );
};

export const getInitFilterValues = (urlParams: any): FilterType => {
  return {
    transactionStatus:
      urlParams?.get('transactionStatus') ?? RequestWithdrawStatus['Tất cả'],
    fromTime:
      urlParams?.get('fromTime') ??
      startOfDay(subMonths(new Date(), 1)).toISOString(),
    toTime: urlParams?.get('toTime') ?? endOfDay(new Date()).toISOString(),
    refreshData: 0,
    pageSize: Number(urlParams?.get('pageSize') ?? DEFAULT_PAGE_SIZE),
    pageIndex: Number(urlParams?.get('pageIndex') ?? DEFAULT_PAGE_INDEX),
  };
};

const getTransactionDetailPath = (transactionCode: string) =>
  MainRouteKeys.PointTransactionDetails.replace(
    ':transactionCode',
    encodeURIComponent(transactionCode),
  );

export const columnsDefined: ColumnDef<WithdrawRequestEntity>[] = [
  {
    accessorKey: 'id',
    header: 'Mã Y/C Rút điểm',
    cell: (info) => (
      <ListDetailLink
        to={MainRouteHelpers.requestWithdrawOwnDetail(info.row.original.id)}
        title={`Xem chi tiết yêu cầu rút điểm ${info.row.original.id}`}
      >
        {info.row.original.id}
      </ListDetailLink>
    ),
    meta: { columnSize: { minWidth: 180 } },
  },
  {
    accessorKey: 'transactions',
    header: 'Mã giao dịch',
    cell: (info) => {
      const transactions = info.row.original.transactions || [];
      if (!transactions.length) return <span>-</span>;

      return (
        <div className='withdraw-transaction-links'>
          {transactions.map((transaction) => (
            <Link
              key={transaction.id}
              to={getTransactionDetailPath(transaction.transactionCode)}
              target='_blank'
              rel='noreferrer'
              title={`${transaction.transactionType} - ${transaction.status}`}
            >
              {transaction.transactionCode}
            </Link>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: 'name',
    header: 'Ngân hàng',
    cell: (info) => {
      // return <div className='text-center'>{info.row.original?.bankAccount?.bank?.shortName} - {info.row.original?.bankAccount?.bank?.name}</div>
      return (
        <div className='text-center'>
          {info.row.original?.bankAccount?.bank?.shortName}
        </div>
      );
    },
  },
  {
    accessorKey: 'accountName',
    header: 'Tên tài khoản',
    cell: (info) => {
      return (
        <div className='text-center'>
          {info.row.original?.bankAccount?.accountName}
        </div>
      );
    },
  },
  {
    accessorKey: 'accountNumber',
    header: 'Số tài khoản',
    cell: (info) => {
      return (
        <div className='text-center'>
          {info.row.original?.bankAccount?.accountNumber}
        </div>
      );
    },
  },
  {
    accessorKey: 'amount',
    header: 'Số tiền', //ell
    cell: (info) => {
      return (
        <div className='textDanger'>
          {info.getValue()
            ? NumberUtils.toFormatNumber(Number(info.getValue()))
            : '0'}
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Trạng thái',
    cell: (info) => (
      <div
        className={
          RequestWithdrawStatusColor[
            info.getValue() as keyof typeof RequestWithdrawStatusColor
          ]
        }
      >
        {String(info.getValue())}
      </div>
    ),
  },
  // {
  //   accessorKey: 'transactions',
  //   header: 'GD rút điểm',
  //   cell: (info) => {
  //     const txs: any  = info.getValue();
  //     console.log(txs)
  //     if(!txs || txs?.length == 0) return <></>
  //     const txReuest = txs.find((tx: any) => tx.transactionType === PointTransactionTypesColor['Rút điểm']);
  //     return <div className="textInfo">{txReuest.id}</div>
  //   }
  // },
  // {
  //   accessorKey: 'transactions',
  //   header: 'GD hoàn điểm',
  //   cell: (info) => {
  //     const txs: any  = info.getValue();
  //     if(!txs || txs?.length == 0) return <></>
  //     const txReuest = txs.find((tx: any) => tx.transactionType === PointTransactionTypesColor['Hoàn điểm']);
  //     return <div className="textDanger">{txReuest.id}</div>
  //   }
  // },
  {
    accessorKey: 'createdAt',
    header: 'Thời gian gửi',
    cell: (info) =>
      format(new Date(String(info.getValue())), 'HH:mm:ss dd/MM/yyyy'),
  },
  {
    accessorKey: 'confirmAt',
    header: 'Thời gian duyệt',
    cell: (info) =>
      info.getValue()
        ? format(new Date(String(info.getValue())), 'HH:mm:ss dd/MM/yyyy')
        : '',
  },
  {
    accessorKey: 'rejectReason',
    header: 'Nguyên nhân từ chối',
    cell: (info) => {
      return (
        <div className='textDanger'>
          {info.getValue() ? String(info.getValue()) : ''}
        </div>
      );
    },
  },
  {
    accessorKey: 'transactionImageUrl',
    header: 'Hình ảnh',
    cell: (info) => {
      if (!info.row.original.transactionImageUrl) return <></>;
      return (
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => {
            window.open(info.row.original.transactionImageUrl, '_blank');
          }}
        >
          <img
            style={{ width: 'auto', height: 25, objectFit: 'contain' }}
            src={info.row.original.transactionImageUrl}
            alt='Hình ảnh'
          />
        </div>
      );
    },
  },
  {
    id: 'action',
    header: 'Thao tác',
    meta: {
      columnSize: {
        width: 170,
        minWidth: 170,
      },
    },
    cell: (info) => {
      return (
        <div className='request-withdraw-own-actions'>
          <ListDetailLink
            className='request-withdraw-own-actions__button'
            to={MainRouteHelpers.requestWithdrawOwnDetail(info.row.original.id)}
            title='Xem chi tiết yêu cầu rút điểm'
          >
            <Icon.ViewDetailIcon />
            Chi tiết
          </ListDetailLink>
        </div>
      );
    },
  },
];

export const convertVariables = (variables: FilterType) => {
  const { fromTime, toTime, ...rest } = variables;

  const newTimeFrom =
    typeof fromTime === 'string' ? new Date(fromTime).getTime() : fromTime;
  const newTimeTo =
    typeof toTime === 'string' ? new Date(toTime).getTime() : toTime;

  return {
    ...rest,
    fromTime: fromTime ? formatDateTime(Number(newTimeFrom)) : '',
    toTime: toTime ? formatDateTime(Number(newTimeTo), true) : '',
  };
};

export const convertSelectStatusOptions = () => {
  return Object.keys(RequestWithdrawStatus).map((key: string) => {
    return {
      label: RequestWithdrawStatus[key as keyof typeof RequestWithdrawStatus],
      value: key,
    };
  });
};
